'use client';

import React, { useState } from 'react';
import { Loader2, Send } from 'lucide-react';

const BRANCHES = [
  'Bloemfontein',
  'Aliwal North',
  'Bethlehem',
  'Welkom',
  'Klerksdorp',
  'Vryburg',
  'Kuruman',
  'Upington',
  'Springbok',
  'De Aar',
  'Kimberly',
];

interface WorkRequestFormProps {
  onSubmit: (data: {
    vehicleReg: string;
    branch: string;
    taskDetails: string;
    scheduledDate: string;
    scheduledTime: string;
  }) => Promise<void>;
  isLoading: boolean;
}

interface FormErrors {
  vehicleReg?: string;
  branch?: string;
  taskDetails?: string;
  scheduledDate?: string;
  scheduledTime?: string;
}

export default function WorkRequestForm({ onSubmit, isLoading }: WorkRequestFormProps) {
  const [vehicleReg, setVehicleReg] = useState('');
  const [branch, setBranch] = useState('');
  const [taskDetails, setTaskDetails] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('08:00');
  const [errors, setErrors] = useState<FormErrors>({});

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!vehicleReg.trim()) newErrors.vehicleReg = 'Vehicle registration is required';
    if (!branch) newErrors.branch = 'Branch is required';
    if (!taskDetails.trim() || taskDetails.trim().length < 10)
      newErrors.taskDetails = 'Task details must be at least 10 characters';
    if (!scheduledDate) newErrors.scheduledDate = 'Scheduled date is required';
    if (!scheduledTime) newErrors.scheduledTime = 'Scheduled time is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit({ vehicleReg: vehicleReg.toUpperCase(), branch, taskDetails, scheduledDate, scheduledTime });
    setVehicleReg('');
    setBranch('');
    setTaskDetails('');
    setScheduledDate('');
    setScheduledTime('08:00');
    setErrors({});
  };

  // Minimum date = today
  const today = new Date().toISOString().split('T')[0];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Vehicle Registration */}
      <div>
        <label className="label-field">Vehicle Registration <span className="text-destructive">*</span></label>
        <input
          type="text"
          value={vehicleReg}
          onChange={(e) => setVehicleReg(e.target.value.toUpperCase())}
          className="input-field font-mono-data uppercase"
          placeholder="e.g. GP 23-441"
          disabled={isLoading}
        />
        {errors.vehicleReg && <p className="field-error">{errors.vehicleReg}</p>}
        <p className="field-hint">South African registration format</p>
      </div>

      {/* Branch */}
      <div>
        <label className="label-field">Branch <span className="text-destructive">*</span></label>
        <select
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
          className="input-field"
          disabled={isLoading}
        >
          <option value="">Select branch…</option>
          {BRANCHES.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        {errors.branch && <p className="field-error">{errors.branch}</p>}
      </div>

      {/* Date + Time row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-field">Scheduled Date <span className="text-destructive">*</span></label>
          <input
            type="date"
            value={scheduledDate}
            min={today}
            onChange={(e) => setScheduledDate(e.target.value)}
            className="input-field"
            disabled={isLoading}
          />
          {errors.scheduledDate && <p className="field-error">{errors.scheduledDate}</p>}
        </div>
        <div>
          <label className="label-field">Start Time <span className="text-destructive">*</span></label>
          <input
            type="time"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            className="input-field"
            disabled={isLoading}
          />
          {errors.scheduledTime && <p className="field-error">{errors.scheduledTime}</p>}
        </div>
      </div>

      {/* Task Details */}
      <div>
        <label className="label-field">Task Details <span className="text-destructive">*</span></label>
        <textarea
          value={taskDetails}
          onChange={(e) => setTaskDetails(e.target.value)}
          className="input-field resize-none"
          rows={4}
          placeholder="Describe the work to be performed — e.g. Full service, oil change, brake inspection, tyre rotation…"
          disabled={isLoading}
          style={{ minHeight: '100px' }}
        />
        {errors.taskDetails && <p className="field-error">{errors.taskDetails}</p>}
        <p className="field-hint">{taskDetails.length} characters — be specific for accurate calendar notes</p>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm transition-all duration-150 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Creating Calendar Event…
          </>
        ) : (
          <>
            <Send size={16} />
            Schedule & Sync to Calendar
          </>
        )}
      </button>
    </form>
  );
}
